// frontend/app/components/lessonPlans/authoring/WizardChrome.js
//
// The header, step rail and footer both authoring wizards wear.
//
// The lesson wizard and the journey wizard are the same shape — a titled
// header with a save, a rail of numbered steps, and a footer that turns into
// publish on the last one. Keeping that in one place is what stops the two
// from drifting into two different-feeling editors of the same product.

import React from "react";
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { ActionButton } from "../blocks/LessonBlockShell";

export const WizardHeader = ({
  colors,
  eyebrow,
  title,
  saving,
  saveLabel,
  closeLabel,
  onClose,
  onSave,
}) => (
  <View
    style={[styles.header, { backgroundColor: colors.panelBg, borderColor: colors.border }]}
  >
    <TouchableOpacity
      onPress={onClose}
      accessibilityRole="button"
      accessibilityLabel={closeLabel}
      style={[styles.closeButton, { borderColor: colors.border }]}
    >
      <MaterialCommunityIcons name="arrow-left" size={18} color={colors.textSecondary} />
    </TouchableOpacity>

    <View style={{ flex: 1, minWidth: 0 }}>
      <Text style={[styles.eyebrow, { color: colors.textMuted }]}>{eyebrow}</Text>
      <Text style={[styles.title, { color: colors.textPrimary }]} numberOfLines={1}>
        {title}
      </Text>
    </View>

    {saving ? <ActivityIndicator size="small" color={colors.accentPrimary} /> : null}
    <ActionButton
      colors={colors}
      label={saveLabel}
      icon="content-save-outline"
      variant="outline"
      disabled={saving}
      onPress={onSave}
    />
  </View>
);

export const WizardStepRail = ({ colors, steps, currentStep, onSelect, stepLabel }) => (
  <View style={styles.stepRail}>
    {steps.map((step) => {
      const isActive = step.num === currentStep;
      const isDone = step.num < currentStep;
      return (
        <TouchableOpacity
          key={step.num}
          onPress={() => onSelect(step.num)}
          accessibilityRole="button"
          accessibilityState={{ selected: isActive }}
          accessibilityLabel={`${stepLabel} ${step.num}: ${step.title}`}
          style={[
            styles.stepChip,
            {
              backgroundColor: isActive ? colors.accentGlow : colors.panelBg,
              borderColor: isActive ? colors.accentPrimary : colors.border,
            },
          ]}
        >
          <MaterialCommunityIcons
            name={isDone ? "check-circle" : step.icon}
            size={14}
            color={isActive || isDone ? colors.accentPrimary : colors.textMuted}
          />
          <Text
            style={[
              styles.stepChipText,
              { color: isActive ? colors.textPrimary : colors.textSecondary },
            ]}
          >
            {step.title}
          </Text>
        </TouchableOpacity>
      );
    })}
  </View>
);

export const WizardFooter = ({
  colors,
  currentStep,
  lastStep,
  onBack,
  onNext,
  onFinish,
  backLabel,
  nextLabel,
  finishLabel,
  finishDisabled,
  note,
}) => (
  <View style={[styles.footer, { borderTopColor: colors.border }]}>
    <ActionButton
      colors={colors}
      label={backLabel}
      icon="arrow-left"
      variant="outline"
      disabled={currentStep === 1}
      onPress={onBack}
    />
    <View style={{ flex: 1 }} />
    {note ? (
      <Text style={[styles.footerNote, { color: colors.textMuted }]}>{note}</Text>
    ) : null}
    {currentStep < lastStep ? (
      <ActionButton colors={colors} label={nextLabel} icon="arrow-right" onPress={onNext} />
    ) : (
      <ActionButton
        colors={colors}
        label={finishLabel}
        icon="check"
        accent={colors.success}
        disabled={finishDisabled}
        onPress={onFinish}
      />
    )}
  </View>
);

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 16,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    margin: 12,
    marginBottom: 0,
    gap: 12,
  },
  closeButton: {
    width: 34,
    height: 34,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  eyebrow: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },
  title: { fontSize: 17, fontWeight: "800", marginTop: 2 },

  stepRail: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    paddingHorizontal: 12,
    paddingTop: 12,
  },
  stepChip: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 7,
    gap: 6,
  },
  stepChipText: { fontSize: 12, fontWeight: "700" },

  footer: {
    flexDirection: "row",
    alignItems: "center",
    borderTopWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 10,
  },
  footerNote: { fontSize: 12, fontWeight: "600", marginRight: 4 },
});
